/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { announcementsAPI, viewsAPI } from '../lib/api';
import { formatDateTime, formatRelativeTime, TARGET_AUDIENCES, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Megaphone, ChevronLeft, Loader2, Trash2, AlertTriangle, Users, Clock, User,
  CalendarDays, Lock, Printer, Tag, Paperclip, FileText, Image as ImageIcon, Download,
} from 'lucide-react';

const AnnouncementDetailPage = () => {
  const navigate = useNavigate();
  const { announcementId } = useParams();
  const { user } = useAuth();
  usePageTitle('Duyuru Detay');
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = ['admin', 'genel_sekreter'].includes(user?.role);

  useEffect(() => { loadAnnouncement(); }, [announcementId]);

  useEffect(() => {
    if (announcementId) {
      viewsAPI.track('announcement', announcementId).catch(() => {});
    }
  }, [announcementId]);

  const loadAnnouncement = async () => {
    try {
      const response = await announcementsAPI.getById(announcementId);
      setAnnouncement(response.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error('Bu duyuruyu goruntuleme yetkiniz yok');
      else toast.error('Duyuru yuklenirken hata olustu');
      navigate('/panel/duyurular');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!confirm('Bu duyuruyu silmek istediginizden emin misiniz?')) return;
    try {
      await announcementsAPI.delete(announcementId);
      toast.success('Duyuru silindi');
      navigate('/panel/duyurular');
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Duyuru silinemedi'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!announcement) return null;

  const priorityConfig = {
    acil: { label: 'Acil', bg: 'bg-red-100 text-red-800 border-red-300', gradient: 'from-red-800 via-red-900 to-red-800', icon: AlertTriangle },
    yuksek: { label: 'Yuksek', bg: 'bg-amber-100 text-amber-800 border-amber-300', gradient: 'from-amber-800 via-amber-900 to-amber-800', icon: Megaphone },
    normal: { label: 'Normal', bg: 'bg-blue-100 text-blue-800 border-blue-300', gradient: 'from-slate-800 via-slate-900 to-slate-800', icon: Megaphone },
    dusuk: { label: 'Dusuk', bg: 'bg-slate-100 text-slate-700 border-slate-300', gradient: 'from-slate-700 via-slate-800 to-slate-700', icon: Megaphone },
  };
  const pCfg = priorityConfig[announcement.priority] || priorityConfig.normal;
  const PIcon = pCfg.icon;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="max-w-5xl mx-auto space-y-6 print-area" data-testid="announcement-detail-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between no-print">
          <Button variant="ghost" size="sm" onClick={() => navigate('/panel/duyurular')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Duyurulara Don
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" /> Yazdir
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete} data-testid="delete-announcement-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            )}
          </div>
        </div>

        {/* Hero Header */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className={`bg-gradient-to-r ${pCfg.gradient} px-6 py-5`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                <PIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={`${pCfg.bg} border px-3 py-0.5 text-xs font-semibold`}>{pCfg.label}</Badge>
                </div>
                <h1 className="text-xl font-bold text-white">{announcement.title}</h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-white/60">
                  <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {announcement.created_by_name || 'Sistem'}</span>
                  <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {formatRelativeTime(announcement.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Acil Alert */}
          {announcement.priority === 'acil' && (
            <div className="bg-red-50 border-b border-red-100 px-6 py-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-red-900">Bu acil bir duyurudur. Lutfen dikkate aliniz.</span>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-slate-500" /> Duyuru Icerigi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{announcement.content}</p>
              </CardContent>
            </Card>

            {/* Ek Dosyalar */}
            {announcement.attachments && announcement.attachments.length > 0 && (
              <Card className="shadow-sm" data-testid="announcement-attachments">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-slate-500" /> Ek Dosyalar ({announcement.attachments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {announcement.attachments.map((a) => {
                    const isImg = ['jpg','jpeg','png'].includes((a.extension || '').toLowerCase());
                    const sizeMb = a.file_size / 1024 / 1024;
                    const sizeStr = sizeMb >= 1 ? `${sizeMb.toFixed(1)} MB` : `${Math.round(a.file_size / 1024)} KB`;
                    const downloadHref = process.env.REACT_APP_BACKEND_URL + a.download_url;
                    return (
                      <a
                        key={a.id}
                        href={downloadHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-lg transition-colors group"
                        data-testid={`download-attachment-${a.id}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isImg ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {isImg ? <ImageIcon className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{a.original_name}</p>
                          <p className="text-xs text-slate-500">.{a.extension} · {sizeStr}</p>
                        </div>
                        <Download className="w-4 h-4 text-slate-400 group-hover:text-amber-600" />
                      </a>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Detaylar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Yayinlayan</p>
                    <p className="text-sm text-slate-900 font-medium">{announcement.created_by_name || 'Sistem'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarDays className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Yayin Tarihi</p>
                    <p className="text-sm text-slate-900">{formatDateTime(announcement.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Tag className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Oncelik</p>
                    <Badge className={`${pCfg.bg} border text-xs mt-0.5`}>{pCfg.label}</Badge>
                  </div>
                </div>
                {announcement.expires_at && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Bitis Tarihi</p>
                      <p className="text-sm text-slate-900">{formatDateTime(announcement.expires_at)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" /> Hedef Kitle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {announcement.target_audience?.map((a) => (
                    <Badge key={a} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs px-3 py-1">
                      {TARGET_AUDIENCES.find((t) => t.value === a)?.label || a}
                    </Badge>
                  ))}
                </div>
                {announcement.target_group_ids?.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-800">Belirli gruplara kisitli erisim</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnouncementDetailPage;
